/**
 * Dashboard Service (Redux Toolkit slice)
 *
 * Why not use createApplicationService here?
 * - createApplicationService is designed for static, application scoped data that is
 *   fetched once and cached for the app lifetime (e.g, branches, users etc).
 * - The dashboard requires parameterized, dynamic queries (teamId/feeEarnerRef/branchId
 *   and date ranges) that change frequently with filters.
 * - It must coordinate multiple endpoints in parallel (Matters, BillingTarget, TimeTarget,
 *   Debtors, Creditors) and update a single cohesive dashboard.
 */
import { createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { API } from "api/axios.config";
import { constructApiQuery } from "api/index";
import store from "../store";

//-------------------------------------------
//           API Functions
//-------------------------------------------
const DASHBOARD_BASE = "/Dashboard";

const fetchDashboardDataAPI = async (filters = {}) => {
  const { teamId, feeEarnerRef, branchId, fromDate, toDate } = filters;

  if (!teamId && !feeEarnerRef && !branchId) {
    return {
      matters: null,
      billing: null,
      time: null,
      debtors: null,
      creditors: null,
    };
  }

  const buildUrl = (endpoint) => {
    let url = `${DASHBOARD_BASE}/${endpoint}?`;
    const parameters = constructApiQuery(
      branchId || null,
      teamId || null,
      feeEarnerRef || null
    );
    if (fromDate) url += `&FromDate=${fromDate}`;
    if (toDate) url += `&ToDate=${toDate}`;
    if (Object.keys(parameters).length) {
      url += `&${Object.keys(parameters)[0]}=${Object.values(parameters)[0]}`;
    }
    return url;
  };

  const [mattersData, billingData, timeData, debtorsData, creditorsData] =
    await Promise.all([
      API.get(buildUrl("Matters")),
      API.get(buildUrl("BillingTarget")),
      API.get(buildUrl("TimeTarget")),
      API.get(buildUrl("Debtors")),
      API.get(buildUrl("Creditors")),
    ]);

  return {
    matters: mattersData.data,
    billing: billingData.data,
    time: timeData.data,
    debtors: debtorsData.data,
    creditors: creditorsData.data,
  };
};

//-------------------------------------------
//           Dashboard Slice
//-------------------------------------------
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    data: null,
    loading: false,
    fetched: false,
    filters: null,
  },
  reducers: {
    setData: (state, action) => {
      state.data = action.payload;
      state.fetched = true;
      state.loading = false;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetData: (state) => {
      state.data = null;
      state.fetched = false;
      state.loading = false;
      state.filters = null;
    },
  },
});

const { setData, setLoading, setFilters, resetData } = dashboardSlice.actions;

export const dashboardReducer = dashboardSlice.reducer;

//-------------------------------------------
//           Action Creators
//-------------------------------------------
export const updateDashboardData = async (rawFilters = {}) => {
  store.dispatch(setLoading(true));

  try {
    const filters = {
      teamId: rawFilters?.teamId ?? null,
      feeEarnerRef: rawFilters?.feeEarnerRef ?? null,
      branchId: rawFilters?.branch ?? rawFilters?.branchId ?? null,
      fromDate: rawFilters?.FromDate ?? rawFilters?.fromDate ?? null,
      toDate: rawFilters?.ToDate ?? rawFilters?.toDate ?? null,
    };

    // Store filters for reference
    store.dispatch(setFilters(filters));

    if (!filters.feeEarnerRef && !filters.teamId && !filters.branchId) {
      store.dispatch(setLoading(false));
      return;
    }

    const data = await fetchDashboardDataAPI(filters);
    store.dispatch(setData(data));
  } catch (error) {
    console.error("Dashboard data fetch failed:", error);
    store.dispatch(setLoading(false));
  }
};

//-------------------------------------------
//           Custom Hook
//-------------------------------------------
export const useDashboardData = () => {
  const { data, loading, fetched, filters } = useSelector(
    (state) => state.dashboard
  );

  return {
    data,
    loading,
    fetched,
    filters,
    refresh: () => filters && updateDashboardData(filters),
    reset: () => store.dispatch(resetData()),
  };
};
